import { z } from 'zod'
import { SemverSchema } from '../shared'
import { UTCMillisecondsSchema } from '../../time'
import { PermissionTypeSchema } from '../../permissions'
import { CompanyIdSchema, UserIdSchema, idSchemaV2 } from '../../ids'
import { TinyCompanySchema } from '../../company'
import { AuditPropsSchema, UPLOAD_URL_DESCRIPTION } from '../../common'
import { ArchivableSchema } from '../../archive'

/**
 * App - Integration application in Terros Connect
 */
export const AppIdSchema = idSchemaV2('App')
export type AppId = z.infer<typeof AppIdSchema>

export const AppStatusSchema = z.enum(['development', 'internal', 'published', 'deprecated'])
export type AppStatus = z.infer<typeof AppStatusSchema>

export const AppValidationStatusSchema = z.enum(['none', 'pending', 'approved', 'rejected'])
export type AppValidationStatus = z.infer<typeof AppValidationStatusSchema>

export const UnsavedAppSchema = z.object({
  name: z.string().nonempty().meta({
    example: 'Salesforce',
    description:
      'A short globally unique name for the app, which will be displayed to users when they install the app and grant permissions. This is required for the App to be published.',
  }),
  description: z.string().optional().meta({
    description:
      "A short markdown summary of the app's functionality and purpose. This is required for the App to be published.",
  }),
  documentation: z.string().optional().meta({
    description: 'Markdown documentation for the app',
  }),
})
export type UnsavedApp = z.infer<typeof UnsavedAppSchema>

export const AppCompanySchema = TinyCompanySchema.pick({
  companyId: true,
  name: true,
})
export type AppCompany = z.infer<typeof AppCompanySchema>

export const AppDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...ArchivableSchema.shape,
  ...UnsavedAppSchema.shape,
  appId: AppIdSchema,
  companyId: CompanyIdSchema,
  company: AppCompanySchema.optional().meta({ description: 'Company that created the app' }),
  status: AppStatusSchema.optional(),
  currentVersion: SemverSchema.optional().meta({
    description: 'The currently published version of the app. This may be empty if no version is published yet.',
  }),
  permissions: z.array(PermissionTypeSchema).meta({
    description: 'List of permissions requested by the app for use in scripts.',
  }),
  avatarUrl: z.string().optional().meta({ id: 'appAvatarUrl' }),
  avatarBlurhash: z.string().optional().meta({ id: 'appAvatarBlurhash' }),
  uploadAvatarUrl: z.string().optional().meta({ description: UPLOAD_URL_DESCRIPTION }),
  privacyPolicy: z.string().optional().meta({
    description: 'URL to the privacy policy. Required for marketplace publication.',
  }),
  termsOfService: z.string().optional().meta({
    description: 'URL to the terms of service. Required for marketplace publication.',
  }),
  validationStatus: AppValidationStatusSchema.optional().meta({
    description: 'Current marketplace validation/review state. Defaults to "none" until the owner submits.',
  }),
  validationNote: z.string().optional().meta({
    description: 'Optional note from the reviewer (typically a rejection reason).',
  }),
  submittedAt: UTCMillisecondsSchema.optional(),
  submittedBy: UserIdSchema.optional(),
  reviewedAt: UTCMillisecondsSchema.optional(),
  reviewedBy: UserIdSchema.optional(),
})
export type AppData = z.infer<typeof AppDataSchema>
